import React, { useEffect, useRef } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/app/store';
import { BreadcrumbTruncationProvider } from 'src/features/header/components/BreadcrumbIA2/BreadcrumbTruncationContext';
import { usePopoverResizeObserver } from 'src/commons/utils/hooks';
import {
  useChromeConfigurationListener,
  useEscalatedPortalZIndex,
  useGlobalHeaderLayout,
  useLayoutResizeObserver,
} from 'src/commons/hooks';
import { ImageProps } from 'src/commons/utils/types';
import { ConnectedConsoleLevelBannerWithAces } from 'src/features/header/components/Banners/Common/BannerWithAces';
import SkipNavCard from 'src/features/header/components/SkipNavCard/SkipNavCard';
import {
  A3ChatContainer,
  GlobalStyle,
  HeaderContainer,
  HeaderPortal,
  MobileHeaderContainer,
} from 'src/features/header/styles';
import {
  ConsoleHeaderContent,
  ToastBannerWrapper,
  useMobileViewPortHook,
} from 'src/features/header/UtilityComponents';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';
import { PortalContextProvider } from '@amzn/storm-ui';
import {
  ATC_EXTERNAL_TOGGLE,
  Components,
  CONSOLE_HEADER_SECTION_ELEMENT_ID,
  DOMAIN,
  REALM,
} from 'src/commons/constants';
import MobileSpoofingInfo from 'src/features/header/components/SpoofingInfo/MobileSpoofingInfo';
import { selectIsSpoofing } from 'src/features/header/store/slice';
import {
  DEVICE_TYPE,
  PAGE_LAYOUT_TYPE,
  selectDeviceType,
  selectIsMobileWebView,
  selectMobileWebSupport,
  selectPageLayoutType,
  selectViewType,
  VIEW_TYPE,
} from 'src/app/slice';
import {
  selectInternalLogoImage,
  selectLogoImage,
} from 'src/features/nav/store/slice';
import { markComponentReady } from 'src/commons/utils/logWithTakt';
import { shouldRenderMenu } from 'src/commons/utils/helpers';
import GlobalNonStickyMobileHeader from 'src/features/header/GlobalNonStickyMobileHeader';
import { fetchAdvertisingApiCsrfToken } from 'src/app/slice.actions';
import { isServerAssembledPage } from 'src/commons/utils';
// a3c-loader's exposeSharedDependencies assigns the whole @amzn/storm-ui
// namespace onto window.A3CShared for remote micro-frontends. That opaque
// object defeats webpack's export analysis, so all 365 StormUI exports are
// marked used and the whole library lands in the entry bundle. Importing the
// loader lazily moves its module tree -- namespace capture included -- into an
// async chunk. Nothing is pruned, so remotes still resolve the full namespace,
// and the loader's internal ordering is unchanged because the chunk is fully
// evaluated before any of its code runs.
const A3Chat = React.lazy(
  () => import(/* webpackChunkName: "a3c-loader" */ '@amzn/a3c-loader'),
);
import { EmbeddedChatManager } from 'src/features/header/components/EmbeddedChatManager/EmbeddedChatManager';
import { ErrorBoundary } from 'src/commons/components/ErrorBoundary';
import { SpotlightDirectorGate } from 'src/features/spotlight/SpotlightDirectorGate';

// Declared at module scope: a component defined inside GlobalHeader is a new
// type on every GlobalHeader render, so React would unmount and remount the
// whole header subtree each time.
const WebviewHeader: React.VFC<{ isSpoofing: boolean }> = ({ isSpoofing }) => (
  <>{isSpoofing && <MobileSpoofingInfo />}</>
);

const MobileHeader: React.VFC<{
  isSpoofing: boolean;
  pageLayoutType: PAGE_LAYOUT_TYPE;
}> = ({ isSpoofing, pageLayoutType }) => (
  <>
    {isSpoofing && <MobileSpoofingInfo />}
    <ConsoleHeaderContent pageLayoutType={pageLayoutType} isMobile={true} />
  </>
);

const DesktopHeader: React.VFC<{
  logo: ImageProps;
  pageLayoutType: PAGE_LAYOUT_TYPE;
  popoverResizeRef: React.RefObject<HTMLDivElement>;
}> = ({ logo, pageLayoutType, popoverResizeRef }) => {
  usePopoverResizeObserver({ popoverResizeRef });
  return (
    <BreadcrumbTruncationProvider>
      <ConsoleHeaderContent
        logo={logo}
        pageLayoutType={pageLayoutType}
        isMobile={false}
      />
    </BreadcrumbTruncationProvider>
  );
};

export const GlobalHeader: React.VFC<{
  viewType: VIEW_TYPE;
  deviceType: DEVICE_TYPE;
  isMobile: boolean;
  isSpoofing: boolean;
  logo: ImageProps;
  internalLogo?: ImageProps;
  isMobileWebView: boolean;
}> = ({
  viewType,
  deviceType,
  isMobile,
  isSpoofing,
  logo,
  internalLogo,
  isMobileWebView,
}) => {
  const { config: internalChromeConfig } = useInternalChromeConfigContext();
  const pageLayoutType = useSelector(selectPageLayoutType);
  const mobileWebSupport = useSelector(selectMobileWebSupport);
  const portalRef = useRef<HTMLDivElement>(null);
  const consoleHeaderSectionRef = useRef<HTMLDivElement>(null);
  const isDesktop = !isMobile && !isMobileWebView;
  const { isInterstitialPage, isInternalView } = internalChromeConfig;
  const isInternalStandard =
    pageLayoutType === PAGE_LAYOUT_TYPE.INTERNAL_STANDARD || isInternalView;
  const showInternalLogo =
    isInternalStandard &&
    window.sessionStorage.getItem(ATC_EXTERNAL_TOGGLE) !== 'true';
  const isFocusLayout =
    pageLayoutType === PAGE_LAYOUT_TYPE.FOCUS ||
    pageLayoutType === PAGE_LAYOUT_TYPE.FOCUS_NO_HEADER;
  const GlobalHeaderContainer = isDesktop
    ? HeaderContainer
    : MobileHeaderContainer;
  const dispatch = useDispatch();

  useChromeConfigurationListener();
  useLayoutResizeObserver({
    consoleHeaderSectionRef,
  });

  // Add margin-top to #sc-content-container on desktop and ax-chrome-global-header on mobile to account for the sticky header height
  useGlobalHeaderLayout(isMobile);

  useMobileViewPortHook({
    deviceType,
    mobileWebSupport,
  });

  useEffect(function () {
    markComponentReady(Components.HEADER);
    dispatch(fetchAdvertisingApiCsrfToken());
  }, []);

  // Escalate the global-header portal's z-index so popovers portalled into it
  // (account / user dropdown, etc.) paint ABOVE a StormUI FocusOverlay. HeaderPortal
  // lives OUTSIDE the fixed HeaderContainer and is `position: relative`, so the adopted
  // z-index forms a stacking context above the overlay; nested popovers escalate again.
  useEscalatedPortalZIndex(portalRef);

  const isFocusNoHeader = pageLayoutType === PAGE_LAYOUT_TYPE.FOCUS_NO_HEADER;
  const isFocusMode = pageLayoutType === PAGE_LAYOUT_TYPE.FOCUS;
  const isEmbeddedEnabled = !isMobile;

  return (
    <>
      <ErrorBoundary name="spotlight-director">
        <SpotlightDirectorGate />
      </ErrorBoundary>
      <HeaderPortal id="AACChromeGlobalHeaderPortal" ref={portalRef} />
      <GlobalStyle
        shouldShiftAppWrapper={
          isMobile || isMobileWebView
            ? false
            : shouldRenderMenu(viewType, pageLayoutType)
        }
        isInterstitialPage={isInterstitialPage}
        isMobile={isMobile}
        shiftContentContainer={!isServerAssembledPage()}
      />
      <SkipNavCard />
      <GlobalHeaderContainer
        id={CONSOLE_HEADER_SECTION_ELEMENT_ID}
        ref={consoleHeaderSectionRef}
      >
        <PortalContextProvider portalElementRef={portalRef}>
          <ConnectedConsoleLevelBannerWithAces />
          {isMobileWebView ? (
            <WebviewHeader isSpoofing={isSpoofing} />
          ) : isMobile ? (
            <MobileHeader
              isSpoofing={isSpoofing}
              pageLayoutType={pageLayoutType}
            />
          ) : (
            <DesktopHeader
              logo={showInternalLogo && internalLogo ? internalLogo : logo}
              pageLayoutType={pageLayoutType}
              popoverResizeRef={consoleHeaderSectionRef}
            />
          )}
        </PortalContextProvider>
      </GlobalHeaderContainer>
      {isMobile && !isFocusLayout && <GlobalNonStickyMobileHeader />}
      <ToastBannerWrapper isMobile={isMobile} />
      {!isFocusNoHeader && isEmbeddedEnabled && (
        <ErrorBoundary name="embedded-chat-manager">
          <EmbeddedChatManager isFocusMode={isFocusMode} />
        </ErrorBoundary>
      )}
      {!isFocusNoHeader && (
        <A3ChatContainer>
          <ErrorBoundary name="a3-chat">
            <React.Suspense fallback={null}>
              <A3Chat
                stage={DOMAIN['prod' as keyof typeof DOMAIN]}
                realm={REALM['us-east-1' as keyof typeof REALM]}
              />
            </React.Suspense>
          </ErrorBoundary>
        </A3ChatContainer>
      )}
    </>
  );
};

const globalHeaderConnector = connect((state: RootState) => {
  return {
    viewType: selectViewType(state),
    deviceType: selectDeviceType(state),
    isMobile: selectViewType(state) === VIEW_TYPE.MOBILE,
    isSpoofing: selectIsSpoofing(state),
    logo: selectLogoImage(state),
    internalLogo: selectInternalLogoImage(state),
    isMobileWebView: selectIsMobileWebView(state),
  };
});

export const ConnectedGlobalHeader = globalHeaderConnector(GlobalHeader);
